import {
  Activity,
  PowerOff,
  Search,
  UserPlus,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "@/components/common/DataTable";
import type { ColumnDef } from "@/components/common/DataTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { toast } from "@/components/ui/Toast";
import { api } from "@/lib/api-client";
import { formatDate } from "@/lib/format";
import { useTranslation } from "@/hooks/useTranslation";
import type { Language } from "@/types";

// ─────────────────────────────────────────────
// UsersManagement — Screen 12 (OcZcn)
//
// User table with invite, search, filter, status toggle.
// Design: §8.5 Screen 12
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────

type UserStatus = "ACTIVE" | "DISABLED";

interface AppUser {
  id: string;
  name: string;
  email: string;
  // API returns role as { id, name } object; older/local code may use a plain string
  role: string | { id?: string; name?: string } | null;
  roleId?: string;
  entity?: string;
  department?: string;
  status: UserStatus;
  lastActive?: string;
  createdAt: string;
}

// The API returns paginated lists as { data, meta: { total, ... } }.
interface UsersResponse {
  data: AppUser[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  };
}

// ── Status badge (local — not in StatusBadge enum) ────

function UserStatusBadge({ status }: { status: UserStatus }) {
  const { t } = useTranslation();
  const isActive = status === "ACTIVE";
  return (
    <span
      className={`inline-flex items-center rounded-badge px-2 py-0.5 text-[12px] font-semibold leading-none ${
        isActive
          ? "bg-success-bg text-success"
          : "bg-hover text-text-muted"
      }`}
    >
      {isActive ? t("status.active") : t("status.disabled")}
    </span>
  );
}

// ── Helper ────────────────────────────────────

function formatRelative(
  iso: string | undefined,
  t: (key: string, params?: Record<string, string | number>) => string
) {
  if (!iso) return t("common.never");
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return t("assessment.minutesAgo", { n: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t("assessment.hoursAgo", { n: hours });
    const days = Math.floor(hours / 24);
    return t("assessment.daysAgo", { n: days });
  } catch {
    return iso;
  }
}

// ── Table columns ─────────────────────────────

function buildColumns(
  onToggleStatus: (user: AppUser) => void,
  onViewLogs: (user: AppUser) => void,
  t: (key: string, params?: Record<string, string | number>) => string,
  language: Language
): ColumnDef<AppUser>[] {
  return [
    {
      key: "user",
      header: t("users.user"),
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col gap-0.5">
          <span className="font-medium text-text-primary">{row.name}</span>
          <span className="text-caption text-text-muted">{row.email}</span>
        </div>
      ),
    },
    {
      key: "role",
      header: t("users.role"),
      cell: (row) => {
        const roleName =
          row.role && typeof row.role === "object"
            ? row.role.name ?? "-"
            : (row.role as string) ?? "-";
        return (
          <span className="inline-flex items-center rounded-badge bg-hover px-2 py-0.5 text-[12px] font-semibold text-text-secondary">
            {roleName}
          </span>
        );
      },
    },
    {
      key: "entity",
      header: t("users.entity"),
      cell: (row) => (
        <span className="text-body text-text-secondary">
          {row.entity ?? row.department ?? "-"}
        </span>
      ),
    },
    {
      key: "status",
      header: t("users.status"),
      cell: (row) => <UserStatusBadge status={row.status} />,
    },
    {
      key: "lastActive",
      header: t("users.lastActive"),
      sortable: true,
      sortAccessor: (row) => row.lastActive ? new Date(row.lastActive) : null,
      cell: (row) => (
        <span className="text-caption text-text-muted">
          {formatRelative(row.lastActive, t)}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: t("common.created"),
      sortable: true,
      sortAccessor: (row) => new Date(row.createdAt),
      cell: (row) => (
        <span className="text-caption text-text-muted">
          {formatDate(row.createdAt, language, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      cell: (row) => (
        <div
          className="flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            title="View activity logs"
            className="flex h-7 w-7 items-center justify-center rounded-control text-text-muted transition-colors hover:bg-hover hover:text-text-secondary"
            onClick={(e) => {
              e.stopPropagation();
              onViewLogs(row);
            }}
          >
            <Activity className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title={row.status === "ACTIVE" ? "Deactivate" : "Enable"}
            className={`flex h-7 w-7 items-center justify-center rounded-control text-text-muted transition-colors ${
              row.status === "ACTIVE"
                ? "hover:bg-danger-bg hover:text-danger"
                : "hover:bg-success-bg hover:text-success"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleStatus(row);
            }}
          >
            <PowerOff className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];
}

// ── Main component ────────────────────────────

export function UsersManagement() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [users, setUsers] = useState<AppUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<UserStatus | "">("");
  const [roles, setRoles] = useState<Array<{ id: string; name: string }>>([]);
  const [toggleTarget, setToggleTarget] = useState<AppUser | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  useEffect(() => {
    api
      .get<{ data: Array<{ id: string; name: string }> }>("/roles")
      .then((res) => setRoles(res.data ?? []))
      .catch(() => {});
  }, []);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.set("query", debouncedSearch);
      if (roleFilter) params.set("roleId", roleFilter);
      if (statusFilter) params.set("status", statusFilter);
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));

      const res = await api.get<UsersResponse>(`/users?${params.toString()}`);
      setUsers(res.data ?? []);
      setTotal(res.meta?.total ?? 0);
      setIsError(false);
    } catch {
      // WP20 (S-147): surface the failure with a retry instead of an empty list.
      setUsers([]);
      setTotal(0);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch, roleFilter, statusFilter, page]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  async function handleToggleStatus() {
    if (!toggleTarget) return;
    setIsToggling(true);
    const isActive = toggleTarget.status === "ACTIVE";
    try {
      if (isActive) {
        await api.post(`/users/${toggleTarget.id}/disable`, {});
      } else {
        await api.post(`/users/${toggleTarget.id}/enable`, {});
      }
      toast.success(
        isActive ? t("users.userDeactivated") : t("users.userEnabled"),
        `${toggleTarget.name} has been ${isActive ? "deactivated" : "enabled"}.`
      );
      setToggleTarget(null);
      fetchUsers();
    } catch (err) {
      toast.error("Failed", err instanceof Error ? err.message : "Unknown error");
    } finally {
      setIsToggling(false);
    }
  }

  const columns = buildColumns(
    (user) => setToggleTarget(user),
    (user) => navigate(`/audit-logs?actorId=${user.id}`),
    t,
    language
  );
  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-[24px] font-semibold text-text-primary">{t("users.title")}</h1>
          <p className="text-body text-text-secondary">
            {t("users.subtitle")}
          </p>
        </div>
        <Button
          leadingIcon={<UserPlus className="h-4 w-4" />}
          onClick={() => navigate("/users/new")}
        >
          {t("users.newUser")}
        </Button>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        <div className="max-w-xs flex-1">
          <Input
            leadingIcon={<Search className="h-4 w-4" />}
            placeholder={t("users.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <SelectRoot
          value={roleFilter || "ALL"}
          onValueChange={(v) => {
            setRoleFilter(v === "ALL" ? "" : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t("common.allRoles")}</SelectItem>
            {roles.map((role) => (
              <SelectItem key={role.id} value={role.id}>
                {role.name}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>

        <SelectRoot
          value={statusFilter || "ALL"}
          onValueChange={(v) => {
            setStatusFilter((v === "ALL" ? "" : v) as UserStatus | "");
            setPage(1);
          }}
        >
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{t("common.allStatuses")}</SelectItem>
            <SelectItem value="ACTIVE">{t("status.active")}</SelectItem>
            <SelectItem value="DISABLED">{t("status.disabled")}</SelectItem>
          </SelectContent>
        </SelectRoot>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={users}
        isLoading={isLoading}
        isError={isError}
        onRetry={fetchUsers}
        emptyState={{
          icon: Users,
          title: t("users.noUsersAdded"),
          description: t("users.noUsersAddedDesc"),
          action: {
            label: `+ ${t("users.newUser")}`,
            onClick: () => navigate("/users/new"),
          },
        }}
        page={page}
        totalPages={totalPages}
        totalItems={total}
        pageSize={pageSize}
        onPageChange={setPage}
      />

      {/* Deactivate / Enable confirm */}
      {toggleTarget && (
        <ConfirmDialog
          open={Boolean(toggleTarget)}
          onOpenChange={(v) => {
            if (!v) setToggleTarget(null);
          }}
          title={
            toggleTarget.status === "ACTIVE"
              ? t("users.deactivateUser")
              : t("users.enableUser")
          }
          description={
            toggleTarget.status === "ACTIVE"
              ? t("users.deactivateConfirm")
              : t("users.enableConfirm")
          }
          confirmLabel={
            toggleTarget.status === "ACTIVE" ? t("users.deactivate") : t("users.enable")
          }
          isDestructive={toggleTarget.status === "ACTIVE"}
          isLoading={isToggling}
          onConfirm={handleToggleStatus}
        />
      )}
    </>
  );
}
